import Button, { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faList } from '@fortawesome/pro-regular-svg-icons/faList'
import type { TrialAssociation } from '@modules/trials/types/TrialAssociation'
import { useState } from 'react'
import { SponsorProfile } from './SponsorProfile'

type PartyToShow = {
  affiliation?: string | null
  isLead?: boolean
  name: string
  role?: string | null
  type: 'collaborator' | 'official'
}

export const WhoIsRunningTheClinicalTrial = ({
  trial,
}: {
  trial: NonNullable<TrialAssociation>
}) => {
  const [expanded, setExpanded] = useState(false)

  // Process collaborators
  const collaboratorParties: PartyToShow[] = (trial.collaborators || [])
    .filter((collaborator) => collaborator.organization?.name)
    .map((collaborator) => ({
      affiliation: collaborator.organization.class || null,
      isLead: collaborator.isLead,
      name: collaborator.organization.name,
      role: collaborator.isLead ? 'Lead Sponsor' : 'Collaborator',
      type: 'collaborator' as const,
    }))

  // Process overall officials
  const officialParties: PartyToShow[] = trial.overallOfficials.map(
    (official) => ({
      affiliation: official.affiliation,
      name: official.name ?? '',
      role: official.role,
      type: 'official' as const,
    }),
  )

  // Add responsible party info if it exists and is not already included
  const responsiblePartyName = trial.responsiblePartyInvestigatorFullName
  if (
    responsiblePartyName &&
    !officialParties.some((party) => party.name === responsiblePartyName)
  ) {
    officialParties.push({
      affiliation: trial.responsiblePartyInvestigatorAffiliation ?? null,
      name: responsiblePartyName,
      role: trial.responsiblePartyInvestigatorTitle ?? 'Principal Investigator',
      type: 'official',
    })
  }

  // Combine all parties
  const allParties: PartyToShow[] = [
    // List lead collaborators first
    ...collaboratorParties.filter((party) => party.isLead),
    // Then other collaborators
    ...collaboratorParties.filter((party) => !party.isLead),
    // Then officials
    ...officialParties,
  ]

  // Filter out duplicates and empty names
  const uniqueParties = allParties.filter((party, index, self) => {
    const name = party.name?.trim()
    return (
      name &&
      !name.toLowerCase().startsWith('call ') &&
      index === self.findIndex((p) => p.name === party.name)
    )
  })

  const moreThanThreeParties = uniqueParties.length > 3

  const partiesToShow = expanded ? uniqueParties : uniqueParties.slice(0, 10)

  if (uniqueParties.length === 0) {
    return null
  }

  return (
    <div className='grid grid-cols-1'>
      <Text
        className='mb-4 text-neutral900'
        element={'h3'}
        styleName='h4'
        value='Who is running the clinical trial?'
      />
      <div className='grid grid-cols-1 gap-4'>
        {partiesToShow.map((party) => (
          <div
            className='rounded-xl border border-neutral200 p-4 lg:rounded-2xl lg:p-6'
            key={party.name}
          >
            <SponsorProfile individual={party} />
          </div>
        ))}

        {moreThanThreeParties && (
          <Button
            className={classMerge(
              buttonClassNames.legacy.accent.neutral,
              'ml-auto rounded-full',
            )}
            onClick={() => {
              setExpanded(!expanded)
            }}
            value={
              <Text
                value={
                  <>
                    <Icon className='mr-2' icon={faList} />
                    {!expanded ? 'View All' : 'Hide'}
                  </>
                }
              />
            }
          />
        )}
      </div>
    </div>
  )
}
